import { isPlatformBrowser } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { slideOutHorizontalAnimation } from '@freelancer/animations';
import { Auth } from '@freelancer/auth';
import type { DatastoreCollection } from '@freelancer/datastore';
import { Datastore } from '@freelancer/datastore';
import type {
  ReferralInvitationsCollection,
  ReferralTokenCollection,
  User,
  UsersCollection,
} from '@freelancer/datastore/collections';
import { GiveGetService } from '@freelancer/give-get';
import { LocalStorage } from '@freelancer/local-storage';
import { FreelancerCurrencyPipe } from '@freelancer/pipes';
import { REFERRER_QUERY_PARAM } from '@freelancer/share';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { CardSize } from '@freelancer/ui/card';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { LinkColor, LinkUnderline } from '@freelancer/ui/link';
import { ModalService, ModalSize } from '@freelancer/ui/modal';
import { SpinnerSize } from '@freelancer/ui/spinner';
import { TextSize } from '@freelancer/ui/text';
import { ToastAlertService, ToastAlertType } from '@freelancer/ui/toast-alert';
import { AvatarSize } from '@freelancer/ui/user-avatar';
import { isDefined } from '@freelancer/utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ReferralInvitationStatusApi } from 'api-typings/common/common';
import { ShellConfig } from 'app/app-shell';
import { LoginOrSignup } from 'app/login-signup/login-signup-modal';
import { TrackingConsentBannerService } from 'app/tracking-consent-banner/tracking-consent-banner.service';
import {
  combineLatest,
  firstValueFrom,
  map,
  type Observable,
  of,
  Subscription,
} from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';

@UntilDestroy({ className: 'GiveGetFooterComponent' })
@Component({
  standalone: false,
  selector: 'app-give-get-footer',
  template: `
    <div
      *ngIf="showGiveGetFooter$ | flAsync"
      class="GiveGetFooter-container"
      [class.GiveGetFooter-slideIn]="isBrowser"
      flTrackingSection="GiveGetFooter"
      [@slideOutHorizontalAnimation]="{
        value: undefined,
        params: {
          duration: 200,
        },
      }"
    >
      <fl-card
        class="GiveGetFooter"
        [edgeToEdge]="true"
        [size]="CardSize.SMALL"
      >
        <div
          class="GiveGetFooter-body"
          [class.GiveGetFooter-darkBackground]="isFooterDarkMode$ | flAsync"
        >
          <ng-container *ngIf="isReady$ | flAsync; else loadingState">
            <ng-container *ngIf="user$ | flAsync as user">
              <ng-container *ngIf="bonusAmount$ | flAsync as bonusAmount">
                <div
                  class="GiveGetFooter-titleContainer"
                  [flMarginBottom]="'xsmall'"
                >
                  <fl-user-avatar
                    [flMarginRight]="'small'"
                    [users]="[user]"
                    [size]="AvatarSize.MID"
                  ></fl-user-avatar>
                  <p
                    class="text-small font-bold tablet:text-marketing_small {{
                      (isFooterDarkMode$ | flAsync)
                        ? 'text-light'
                        : 'text-color-inherit'
                    }}"
                    i18n="Give get footer header"
                    [flMarginRight]="'xsmall'"
                  >
                    {{ user.displayName }} has given you
                    {{ bonusAmount }}
                    to try Freelancer
                  </p>
                </div>
                <p
                  class="text-xsmall tablet:text-small {{
                    (isFooterDarkMode$ | flAsync)
                      ? 'text-light'
                      : 'text-color-inherit'
                  }}"
                  i18n="Give get footer text"
                  [flMarginBottom]="'small'"
                >
                  Get started today and use your
                  {{ bonusAmount.split(' ')[0] }} to get anything done! <br />
                  Valid for new users only,
                  <fl-link
                    flTrackingLabel="GiveGetFooter-T&C"
                    [size]="TextSize.XSMALL"
                    [sizeTablet]="TextSize.SMALL"
                    [color]="
                      (isFooterDarkMode$ | flAsync)
                        ? LinkColor.LIGHT
                        : LinkColor.INHERIT
                    "
                    [link]="'/get/' + user.username"
                    [underline]="LinkUnderline.ALWAYS"
                    [queryParams]="{
                      ref: 'social-referral-terms',
                    }"
                  >
                    T&Cs apply.
                  </fl-link>
                </p>

                <div class="GiveGetFooter-cta-container">
                  <div
                    class="GiveGetFooter-cta-fullWidth"
                    [flMarginBottom]="'xsmall'"
                    [flMarginBottomTablet]="'none'"
                    [flMarginRight]="'none'"
                    [flMarginRightTablet]="'large'"
                  >
                    <fl-button
                      i18n="Claim bonus amount"
                      flTrackingLabel="GiveGetFooter-ClaimBonus"
                      [color]="ButtonColor.PRIMARY"
                      [display]="'block'"
                      [size]="ButtonSize.SMALL"
                      [sizeTablet]="ButtonSize.MID"
                      (click)="openLoginSignupModal()"
                    >
                      Claim my
                      {{ bonusAmount.split(' ')[0] }}
                    </fl-button>
                  </div>
                  <fl-link
                    i18n="What can user do with bonus amount"
                    flTrackingLabel="GiveGetFooter-WhatCanIDoWithIt"
                    [color]="LinkColor.PRIMARY"
                    [link]="'/get/' + user.username"
                    [queryParams]="{
                      ref: 'social-referral-terms',
                    }"
                    [size]="TextSize.XXSMALL"
                    [sizeTablet]="TextSize.XSMALL"
                    [flMarginBottom]="'xxsmall'"
                    [flMarginBottomTablet]="'none'"
                    >What can I do with it?
                  </fl-link>
                </div>
              </ng-container>
            </ng-container>
          </ng-container>
        </div>

        <fl-icon
          class="GiveGetFooter-closeIcon"
          flTrackingLabel="GiveGetter-CloseIcon"
          label="Close icon"
          i18n-label="Close icon"
          [color]="
            (isFooterDarkMode$ | flAsync)
              ? IconColor.LIGHT
              : IconColor.FOREGROUND
          "
          [name]="'ui-close'"
          [size]="IconSize.SMALL"
          [hoverAnimation]="'spin-and-highlight'"
          (click)="closeFooter()"
        ></fl-icon>
      </fl-card>
    </div>

    <ng-template #loadingState>
      <fl-spinner
        class="GiveGetFooter-loadingState"
        flTrackingLabel="GiveGetFooterLoading"
        [size]="SpinnerSize.LARGE"
      ></fl-spinner>
    </ng-template>
  `,
  styleUrls: ['./give-get-footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [slideOutHorizontalAnimation],
})
export class GiveGetFooterComponent implements OnInit, OnDestroy {
  AvatarSize = AvatarSize;
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  CardSize = CardSize;
  IconColor = IconColor;
  IconSize = IconSize;
  LinkColor = LinkColor;
  LinkUnderline = LinkUnderline;
  SpinnerSize = SpinnerSize;
  TextSize = TextSize;

  isBrowser = false;
  user$: Observable<User>;
  bonusAmount$: Observable<string | null>;

  isFooterDarkMode$: Observable<boolean>;
  isReady$: Observable<boolean>;
  showGiveGetFooter$: Observable<boolean>;
  userCollection: DatastoreCollection<UsersCollection>;

  private subscriptions = new Subscription();

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private activatedRoute: ActivatedRoute,
    private auth: Auth,
    private cd: ChangeDetectorRef,
    private currencyPipe: FreelancerCurrencyPipe,
    private datastore: Datastore,
    private giveGetService: GiveGetService,
    private localStorage: LocalStorage,
    private modalService: ModalService,
    private router: Router,
    private shellConfig: ShellConfig,
    private toastAlertService: ToastAlertService,
    private trackingConsentBannerService: TrackingConsentBannerService,
  ) {}

  ngOnInit(): void {
    // changes card theme opposite to the page theme to emphasize the card
    this.isFooterDarkMode$ = this.shellConfig
      .getConfig(this.activatedRoute)
      .pipe(map(config => !config.isDarkMode));

    if (isPlatformBrowser(this.platformId)) {
      this.isBrowser = true;

      const localUsername$ = this.localStorage.get('giveGetReferrerUsername');

      const latestQueryParamLocaStorage$ = combineLatest([
        this.activatedRoute.queryParams.pipe(
          map(queryParamMap => queryParamMap[REFERRER_QUERY_PARAM]),
        ),
        localUsername$,
      ]);

      const referrerUsername$ = latestQueryParamLocaStorage$.pipe(
        switchMap(async ([queryParamUserName, localUsername]) => {
          return queryParamUserName || localUsername;
        }),
        filter(isDefined),
      );

      const referralTokenCollection =
        this.datastore.collection<ReferralTokenCollection>(
          'referralToken',
          query =>
            query.search(
              referrerUsername$.pipe(
                map(username => ({
                  referrer_username: username,
                })),
              ),
            ),
        );

      const validUsername$ = combineLatest([
        referralTokenCollection.status$,
        referrerUsername$,
      ]).pipe(
        filter(
          ([status, _]) =>
            (isDefined(status.error) && !status.error) || status.ready,
        ),
        map(([_, username]) => username),
      );

      const userCollection = this.datastore.collection<UsersCollection>(
        'users',
        query => query.where('username', 'equalsIgnoreCase', validUsername$),
      );

      this.user$ = userCollection.valueChanges().pipe(
        map(users => users[0]),
        filter(isDefined),
      );

      const giveGetFooterClosed$ = this.localStorage.get('giveGetFooterClosed');

      this.showGiveGetFooter$ = combineLatest([
        validUsername$,
        giveGetFooterClosed$,
        this.auth.isLoggedIn(),
        this.localStorage.get('trackingConsent'),
      ]).pipe(
        switchMap(
          async ([username, footerClosed, isLoggedIn, trackingConsent]) => {
            // Only check local storage for trackingConsent if supported
            const consent =
              (await this.trackingConsentBannerService.isSupported())
                ? trackingConsent
                : true;

            return (
              !isLoggedIn &&
              isDefined(username) &&
              !footerClosed &&
              isDefined(consent) // Only show give get footer when tracking consent is given
            );
          },
        ),
      );

      const currencyToUse$ = this.giveGetService.getCurrencyToUse();
      const bonusAmountValue$ =
        this.giveGetService.getBonusAmount(currencyToUse$);
      this.bonusAmount$ = combineLatest([
        currencyToUse$,
        bonusAmountValue$,
      ]).pipe(
        map(([currency, bonusAmount]) => {
          return this.currencyPipe.transform(bonusAmount, currency.code, {
            withPrecision: false,
          });
        }),
      );

      this.isReady$ = combineLatest([this.user$, this.bonusAmount$]).pipe(
        map(([user, bonusAmount]) => isDefined(user) && isDefined(bonusAmount)),
      );
      this.subscriptions.add(
        this.user$.subscribe(user =>
          this.localStorage.set('giveGetReferrerUsername', user.username),
        ),
      );
      this.subscriptions.add(
        latestQueryParamLocaStorage$.subscribe(
          ([queryParam, giveGetReferrer]) => {
            if (queryParam && queryParam !== giveGetReferrer) {
              this.localStorage.set('giveGetFooterClosed', false);
            }
          },
        ),
      );

      this.giveGetService.setReferralTokenCookie();
    } else {
      this.showGiveGetFooter$ = of(false);
    }
  }

  async closeFooter(): Promise<void> {
    this.showGiveGetFooter$ = of(false);

    const user = await firstValueFrom(this.user$.pipe(untilDestroyed(this)));
    const bonusAmount = await firstValueFrom(
      this.bonusAmount$.pipe(untilDestroyed(this)),
    );

    this.modalService
      .open('GiveGetEmailModal', {
        closeable: false,
        inputs: {
          bonusAmount: bonusAmount?.split(' ')[0],
          referrerDisplayName: user.displayName,
        },
        size: ModalSize.SMALL,
      })
      .afterClosed()
      .then((emailAddress: string) => {
        if (emailAddress) {
          this.datastore
            .createDocument<ReferralInvitationsCollection>(
              'referralInvitations',
              {
                id: emailAddress,
                email: emailAddress,
                status: ReferralInvitationStatusApi.PENDING,
                canResend: true,
              },
              {
                referrerUsername: user.username,
              },
            )
            .then(response => {
              if (response.status === 'success') {
                this.toastAlertService.open({
                  message: $localize`Thanks! We’ve emailed you a link to claim your free credit.`,
                });
                this.localStorage.set('giveGetFooterClosed', true);
              } else {
                this.showGiveGetFooter$ = of(true);

                // Mark for check to ensure the footer is visible
                this.cd.markForCheck();

                this.toastAlertService.open({
                  type: ToastAlertType.ERROR,
                  message: $localize`Something went wrong while sending your email. Please try again.`,
                });
              }
            });
        } else {
          this.localStorage.set('giveGetFooterClosed', true);
        }
      });
  }

  openLoginSignupModal(): void {
    this.showGiveGetFooter$ = of(false);
    this.modalService
      .open('LoginSignupModal', {
        closeable: true,
        edgeToEdge: true,
        mobileFullscreen: true,
        inputs: {
          form: LoginOrSignup.SIGNUP,
          enableLoginSignupSwitch: false,
        },
        size: ModalSize.XSMALL,
      })
      .afterClosed()
      .then(user => {
        if (user) {
          // The UserSignup post flow manages adding the new user as a referral child.
          // Therefore, we do not need an email pop-up after the user successfully signs up.
          this.localStorage.set('giveGetFooterClosed', true);
          this.router.navigate([`/new-client`]);
        } else {
          this.showGiveGetFooter$ = of(true);
        }
      });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
